
import { Component } from 'vue-property-decorator';
import WizardContentView
  from '@/ui/components/wizards/baseComponents/WizardContentView.vue';
import { mixins } from 'vue-class-component';
import { Validation } from '@/ui/mixins/validation';
import { Action, Getter, Mutation, State } from 'vuex-class';
import { ITenantWizardState } from '@/store/modules/tenantWizard/types';
import { cloneDeep } from 'lodash';
import InfoTooltip from '@/ui/components/components/InfoTooltip.vue';
import { initFlatsAddresses, hasArrayDuplicateValue, filterNumberFromString } from '@/utils/tenantWizardUtilsFunctions';
import { counterLimits } from '@/ui/components/wizards/tenantWizard/wizardSettings/limits';
import { IFlatsConfig, ITenantModel } from '@/types/wizards/tenant.types';
import { IProject } from '@/types/project.types';

@Component({
  components: {
    WizardContentView,
    InfoTooltip,
  },
})
export default class WarmWater extends mixins(Validation) {
  @State('tenantWizard') tenantState!: ITenantWizardState;
  @Getter('tenantWizard/isAutoMappingEnabled') isAutoMappingEnabled!: boolean;
  @Getter('variables/variablesForComboBox') noAutomappingVaribles!: [];
  @Getter('projects/project') project!: IProject;
  @Mutation('tenantWizard/updateTenantObject') updateTenantObject!: (data: ITenantModel) => void;
  @Action('variables/fetchVariables') fetchVariables!: (projectId: string) => Promise<void>;
  @Action('tenantWizard/handleIncrement') handleIncrement!: () => void;
  @Action('tenantWizard/handleDecrement') handleDecrement!: () => void;

  localCounterLimits = counterLimits;
  valid = false;
  sideCosts = 0;
  flatsConfigList: IFlatsConfig[] = [];
  flatsFullAddress: Record<string, string> = {};
  overallServiceWaterCounterIdArray: any = [];

  overallServiceWaterCounterNumberList: any[] = [];

  valueNotAllowedCheck(value: any) {
    if (!this.isAutoMappingEnabled) {
      return true;
    }
    const number = parseInt(value, 10);
    if (!this.variablesForComboBox.includes(number)) {
      return this.$t('tenantWizard.valueNotAllowed');
    }
    return true;
  }

  get counterAmount() {
    let otherCounterNumber = 0;
    let flatCounterNumber = 0;

    if (this.isAutoMappingEnabled) {
      otherCounterNumber = this.overallServiceWaterCounterNumberList.length + 2;
    } else {
      otherCounterNumber = this.overallServiceWaterCounterIdArray.length + 2;
    }

    this.flatsConfigList.forEach((flat: IFlatsConfig) => {
      flatCounterNumber += flat.counters.serviceWaterCounter.length;
    });
    return otherCounterNumber + flatCounterNumber;
  }

  get variablesForComboBox() {
    // if automapping is disabled we just return normal meassurement variables
    if (!this.isAutoMappingEnabled) {
      return this.noAutomappingVaribles;
    }
    // if automapping is enabled we return all numbers from 1 to 255
    return Array.from({ length: 255 }, (_, index) => index + 1);
  }

  get hasDuplicateValue(): boolean | string {
    // will contain all numbers that are set either inside overall counters or inside flats counters
    let array = [this.overallServiceWaterCounterNumberList.map((value: { id: string; name: string}) => value.id)].flat();
    // if automapping is disabled we disable this validation
    if (!this.isAutoMappingEnabled) {
      array = [this.overallServiceWaterCounterIdArray.map((value: { id: string; name: string}) => value.id)].flat();
    } else {
      array = [this.overallServiceWaterCounterNumberList.map((value: { id: string; name: string}) => value.id)].flat();
    }

    // add flats counters
    this.flatsConfigList.forEach((flat: IFlatsConfig) => {
      flat.counters.serviceWaterCounter.forEach((counter: any) => {
        array.push(counter.id);
      });
    });

    return hasArrayDuplicateValue(array, 'tenantWizard.hasDuplicateValue');
  }

  get hasDuplicateName(): boolean | string {
    // will contain all numbers that are set either inside overall counters or inside flats counters
    let array = [];

    if (this.isAutoMappingEnabled) {
      array = [this.overallServiceWaterCounterNumberList.map((value: { id: string; name: string}) => value.name)].flat();
    } else {
      array = [this.overallServiceWaterCounterIdArray.map((value: { id: string; name: string}) => value.name)].flat();
    }

    // add flats counters
    this.flatsConfigList.forEach((flat: IFlatsConfig) => {
      flat.counters.serviceWaterCounter.forEach((counter: any) => {
        array.push(counter.name);
      });
    });

    return hasArrayDuplicateValue(array, 'tenantWizard.hasDuplicateName') ?? true;
  }

  created() {
    this.fetchVariables(this.$route.params.id);
    this.flatsConfigList = this.tenantState.tenant.general.flats.flatConfigurations;
    this.flatsConfigList.forEach((flat: IFlatsConfig, index: number) => {
      if (this.flatsConfigList[index].counters?.serviceWaterCounter === null) {
        this.flatsConfigList[index].counters.serviceWaterCounter = [];
      }
    });
    if (this.flatsConfigList[0].counters.serviceWaterCounter.length === 0) {
      this.flatsConfigList.forEach((flat: IFlatsConfig) => {
        flat.counters.serviceWaterCounter.push({ id: '', name: '' });
      });
    }
    if (this.tenantState.tenant.general.overallCounters.serviceWaterCounter !== null) {
      this.overallServiceWaterCounterIdArray = this.tenantState.tenant.general.overallCounters.serviceWaterCounter;
    }
    if (this.isAutoMappingEnabled) {
      this.fillNumbers();
    }
  }

  fillNumbers() {
    // overall part
    this.overallServiceWaterCounterNumberList = this.overallServiceWaterCounterIdArray.map((counter: any) => {
      return { id: filterNumberFromString(counter.id), name: counter.name };
    });
    // flats part
    this.flatsConfigList.forEach((flat: IFlatsConfig) => {
      flat.counters.serviceWaterCounter.forEach((counter: any) => {
        counter.id = filterNumberFromString(counter.id);
      });
    });
  }

  removeOverallItem(index: number) {
    if (this.isAutoMappingEnabled) {
      this.overallServiceWaterCounterNumberList.splice(index, 1);
    } else {
      this.overallServiceWaterCounterIdArray.splice(index, 1);
    }
    this.$nextTick(() => {
      (this.$refs.form as any).validate();
    });
  }

  addOverallItem() {
    if (this.isAutoMappingEnabled) {
      this.overallServiceWaterCounterNumberList.push({ id: '', name: '' });
    } else {
      this.overallServiceWaterCounterIdArray.push({ id: '', name: '' });
    }
    this.$nextTick(() => {
      (this.$refs.form as any).validate();
    });
  }

  removeCounterFromFlat(flatIndex: number, counterIndex: number) {
    this.flatsConfigList[flatIndex].counters.serviceWaterCounter.splice(counterIndex, 1);
    this.$nextTick(() => {
      (this.$refs.form as any).validate();
    });
  }

  addCounterToFlat(flatIndex: number) {
    this.flatsConfigList[flatIndex].counters.serviceWaterCounter.push({ id: '', name: '' });
    this.$nextTick(() => {
      (this.$refs.form as any).validate();
    });
  }

  mounted() {
    this.flatsFullAddress = initFlatsAddresses(this.flatsConfigList);
    this.$nextTick(() => {
      (this.$refs.form as any).validate();
    });
  }

  handleBack() {
    // if we are going back and we are in the automapping mode we need to convert the numbers to the variable names
    if (this.isAutoMappingEnabled) {
      this.convertNumbersToVariables();
    }
    this.handleDecrement();
  }

  handleNext() {
    if (this.isAutoMappingEnabled) {
      this.convertNumbersToVariables();
    }
    this.updateTenant();
    this.handleIncrement();
  }

  convertNumbersToVariables() {
    // overall part
    this.overallServiceWaterCounterIdArray = this.overallServiceWaterCounterNumberList.map((counter: any) => {
      return { id: `prgHWM.lrCounter_HW_C_${counter.id}`, name: counter.name };
    });
    // flats part
    this.flatsConfigList.forEach((flat: IFlatsConfig) => {
      flat.counters.serviceWaterCounter.forEach((counter: any) => {
        counter.id = `prgHWM.lrCounter_HW_C_${counter.id}`;
      });
    });
  }

  updateTenant() {
    const tenantCopy = cloneDeep(this.tenantState.tenant);
    tenantCopy.general.flats.flatConfigurations = this.flatsConfigList;
    tenantCopy.general.overallCounters.serviceWaterCounter = this.overallServiceWaterCounterIdArray;
    this.updateTenantObject(tenantCopy);
  }
}
